import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Cookies from "js-cookie";
import { message } from "antd";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

// --- API RESPONSE TYPES ---
export interface CallType {
  service_ids: number;
  service_short_description: string;
  service_long_description: string;
}

export interface Member {
  pikitup_member_id: number;
  member_first_name: string;
  member_surname: string;
  member_type_name: string;
  member_type: number;
}

export interface UnitGroup {
  pikitup_unit_group_id: number;
  unit_group_name: string;
  call_types: CallType[];
  pikitup_depot_id: number | null;
  status_flg: number; // 1 = Active, 0 = Inactive
  created_date: string;
  modified_date: string;
  is_my_group: boolean;
  members: Member[];
}

export interface UnitGroupResponse {
  statusCode: number;
  message: string;
  data: {
    meta: any;
    unitGroups: UnitGroup[];
  };
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
}

export interface CreateUnitGroupRequest {
  action: "create";
  unit_group_name: string;
  service_ids: number[];
  member_ids: number[];
  unit_group_description?: string;
  depot_id: number | any;
  escalation_member_ids: number[];
}

export interface UpdateUnitGroupRequest {
  action: "update";
  unit_group_id: number;
  unit_group_name?: string;
  service_ids?: number[];
  member_ids: number[];
  unit_group_description?: string;
  depot_id: number | any;
  escalation_member_ids: number[];
}

export interface DeleteUnitGroupRequest {
  action: "delete";
  unit_group_id: number;
}

export interface ActivateDeactivateUnitGroupRequest {
  action: "activate" | "deactivate";
  unit_group_id: any;
}

// Update your Unit interface to use numbers for IDs
export interface Unit {
  id?: number; // Changed from string to number
  name: string;
  callType: number[]; // Changed from string[] to number[]
  members: number[]; // Changed from string[] to number[]
  description: string;
}

export type UnitGroupMutationRequest =
  | CreateUnitGroupRequest
  | UpdateUnitGroupRequest
  | DeleteUnitGroupRequest
  | ActivateDeactivateUnitGroupRequest;

interface UnitGroupFilters {
  page?: number;
  perPage?: number;
  status?: number;
  unit_group_name?: string;
  reference_no?: string;
  service_id?: number[];
  depot_id?: number;
  status_in?: number[];
  status_not_in?: number[];
  reference_no_in?: string[];
  reference_no_not_in?: string[];
  name_in?: string[];
  name_not_in?: string[];
  created_date_in?: string[];
  created_date_not_in?: string[];
  call_types_in?: string[];
  call_types_not_in?: string[];
  members_in?: number[];
  members_not_in?: number[];
}
// --- AXIOS INSTANCE ---
const api = axios.create({
  baseURL: BASE_URL,
});

const fetchUnitGroups = async (
  filters: UnitGroupFilters = {},
  paginate = true
) => {
  const token = Cookies.get("userToken");
  if (!token) throw new Error("Unauthorized");

  const params = paginate
    ? {
        paginate: true,
        page: filters.page ?? 1,
        per_page: filters.perPage ?? 10,
        ...filters,
      }
    : { ...filters };

  const { data } = await api.get<UnitGroupResponse>("/unit-groups/v1", {
    headers: { Authorization: `Bearer ${token}` },
    params,
    paramsSerializer: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          searchParams.append(key, `[${value.join(",")}]`);
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    },
  });

  return data;
};

export const useUnitGroups = (
  filters?: any,
  paginate?: boolean,
  currentPage?: number,
  perPage?: number
) => {
  return useQuery({
    queryKey: [
      "unitGroups",
      paginate ? { filters, paginate, currentPage, perPage } : { filters },
    ],
    queryFn: () =>
      fetchUnitGroups(
        paginate
          ? {
              ...filters,
              page: currentPage,
              perPage: perPage,
            }
          : { ...filters },
        paginate
      ),
    staleTime: 5000,
  });
};

const createUnitGroup = async (requestData: CreateUnitGroupRequest) => {
  const token = Cookies.get("userToken");
  if (!token) throw new Error("Unauthorized: No user token found");

  const { data } = await api.post("/update-unit-group/v1", requestData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data;
};

const updateUnitGroup = async (requestData: UpdateUnitGroupRequest) => {
  const token = Cookies.get("userToken");
  if (!token) throw new Error("Unauthorized: No user token found");

  const { data } = await api.post("/update-unit-group/v1", requestData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data;
};

const deleteUnitGroup = async (requestData: DeleteUnitGroupRequest) => {
  const token = Cookies.get("userToken");
  if (!token) throw new Error("Unauthorized: No user token found");

  const { data } = await api.post("/update-unit-group/v1", requestData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data;
};

const activateDeactivateUnitGroup = async (
  requestData: ActivateDeactivateUnitGroupRequest
) => {
  const token = Cookies.get("userToken");
  if (!token) throw new Error("Unauthorized: No user token found");

  const { data } = await api.post("/update-unit-group/v1", requestData, {
    headers: { Authorization: `Bearer ${token}` },
  });

  return data;
};

// --- REACT QUERY MUTATION HOOKS ---
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const useCreateUnitGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUnitGroup,
    onSuccess: () => {
      // Invalidate and refetch unit groups after successful creation
      queryClient.invalidateQueries({ queryKey: ["unitGroups"] });
    },
    onError: (error: any) => {
      // handle specific backend validation errors
      if (error?.response?.status === 422) {
        const backendMessage =
          error?.response?.data?.message ||
          "A unit group with the same depot and service already exists.";
        message.warning(backendMessage);
      } else if (error?.response?.status === 401) {
        message.error("Unauthorized. Please log in again.");
      } else {
        message.error(error?.message || "Something went wrong!");
      }
    },
  });
};

export const useUpdateUnitGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUnitGroup,
    onSuccess: () => {
      // Invalidate and refetch unit groups after successful update
      queryClient.invalidateQueries({ queryKey: ["unitGroups"] });
    },
  });
};

export const useDeleteUnitGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteUnitGroup,
    onSuccess: () => {
      // Invalidate and refetch unit groups after successful deletion
      queryClient.invalidateQueries({ queryKey: ["unitGroups"] });
    },
  });
};

export const useActivateDeactivateUnitGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: activateDeactivateUnitGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unitGroups"] });
    },
  });
};

export interface MemberResponse {
  statusCode: number;
  message: string;
  data: {
    members: Member[];
  };
}

const fetchMembers = async () => {
  const token = Cookies.get("userToken");
  if (!token) throw new Error("Unauthorized: No user token found");

  const { data } = await api.get<MemberResponse>("/members/v1", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      member_types_in: [6, 4, 1],
      client_type: "ADMIN",
    },
    paramsSerializer: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Serialize arrays as [1,2,3]
          searchParams.append(key, `[${value.join(",")}]`);
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    },
  });

  return data.data.members.map((m) => ({
    pikitup_member_id: Number(m.pikitup_member_id),
    member_first_name: m.member_first_name,
    member_surname: m.member_surname,
    member_type_name: m.member_type_name,
  }));
};

// --- REACT QUERY HOOK ---
export const useMembers = () => {
  return useQuery({
    queryKey: ["members", 6],
    queryFn: fetchMembers,
  });
};
